import type { Appointment, Customer } from "../types";
import { addIsoDays } from "./date";

export type AppointmentPreferenceConflictCode =
  | "SELECTED_REQUIRES_EXCLUSIVE"
  | "EXISTING_REQUIRES_EXCLUSIVE"
  | "SELECTED_AVOIDS_MALE"
  | "EXISTING_AVOIDS_MALE";

export interface AppointmentPreferenceConflict {
  code: AppointmentPreferenceConflictCode;
  date: string;
  customerId: string;
  customerName: string;
  appointmentId: string;
}

interface AppointmentPreferenceInput {
  selectedCustomer?: Customer;
  customers: Customer[];
  appointments: Appointment[];
  date: string;
  time: string;
  repeatCount: number;
  excludeAppointmentId?: string;
}

function minutes(value: string) {
  const [hour, minute] = value.split(":").map(Number);
  return hour * 60 + minute;
}

function occupiesSlot(
  appointment: Appointment,
  date: string,
  selectedStart: number,
  excludeAppointmentId?: string
) {
  if (
    appointment.id === excludeAppointmentId ||
    appointment.date !== date ||
    appointment.status === "cancelled" ||
    (appointment.entryType === "hold" && appointment.holdStatus !== "PENDING")
  ) return false;

  const selectedEnd = selectedStart + 60;
  const appointmentStart = minutes(appointment.time);
  const appointmentEnd = minutes(appointment.endTime);
  return selectedStart < appointmentEnd && appointmentStart < selectedEnd;
}

export function findAppointmentPreferenceConflicts({
  selectedCustomer,
  customers,
  appointments,
  date,
  time,
  repeatCount,
  excludeAppointmentId
}: AppointmentPreferenceInput): AppointmentPreferenceConflict[] {
  if (!selectedCustomer || !date || !time || repeatCount < 1) return [];

  const selectedStart = minutes(time);
  const customerById = new Map(customers.map((customer) => [customer.id, customer]));
  const conflicts: AppointmentPreferenceConflict[] = [];
  const seen = new Set<string>();

  const addConflict = (
    code: AppointmentPreferenceConflictCode,
    occurrenceDate: string,
    owner: Customer,
    appointmentId: string
  ) => {
    const key = `${occurrenceDate}:${appointmentId}:${code}`;
    if (seen.has(key)) return;
    seen.add(key);
    conflicts.push({
      code,
      date: occurrenceDate,
      customerId: owner.id,
      customerName: owner.name,
      appointmentId
    });
  };

  for (let index = 0; index < repeatCount; index += 1) {
    const occurrenceDate = addIsoDays(date, index * 7);
    const occupied = appointments.filter((appointment) =>
      occupiesSlot(appointment, occurrenceDate, selectedStart, excludeAppointmentId)
    );

    for (const appointment of occupied) {
      if (appointment.customerId === selectedCustomer.id) continue;
      const existingCustomer = customerById.get(appointment.customerId);
      if (!existingCustomer) continue;

      if (selectedCustomer.requiresExclusiveSlot) {
        addConflict("SELECTED_REQUIRES_EXCLUSIVE", occurrenceDate, selectedCustomer, appointment.id);
      }
      if (existingCustomer.requiresExclusiveSlot) {
        addConflict("EXISTING_REQUIRES_EXCLUSIVE", occurrenceDate, existingCustomer, appointment.id);
      }
      if (selectedCustomer.avoidsMaleCoAttendee && existingCustomer.gender === "Man") {
        addConflict("SELECTED_AVOIDS_MALE", occurrenceDate, selectedCustomer, appointment.id);
      }
      if (existingCustomer.avoidsMaleCoAttendee && selectedCustomer.gender === "Man") {
        addConflict("EXISTING_AVOIDS_MALE", occurrenceDate, existingCustomer, appointment.id);
      }
    }
  }

  return conflicts;
}

